import * as React from 'react'
import { Row, Col, Input, Button, Icon, Select, Switch, message, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { ImageUploadModal } from '../../../components/ImageUploadModal'
import { Province } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../utilities/helpers'
import { Routes } from '../../../constants/Routes'
import Helmet from 'react-helmet'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  logoImageUrl: string | null
  showImageModal: boolean
  name: string
  gstNumber: string | null
  province: Province | null
  isOnboarding: boolean
  isLoading: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class General extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!
  user = this.props.currentUserStore.currentUser!

  constructor(props: Props) {
    super(props)

    this.state = {
      logoImageUrl: this.company.logoImageUrl,
      showImageModal: false,
      name: this.company.name,
      gstNumber: this.company.gstNumber,
      province: this.company.province,
      isOnboarding: this.company.isOnboarding,
      isLoading: false,
    }

    this.reloadCompany()
  }

  componentDidMount() {
    this.reloadCompany()
  }

  async reloadCompany() {
    const newCompany = await this.props.currentCompanyStore!.getById(this.company.id)
    this.setState({
      isLoading: false,
      logoImageUrl: newCompany.logoImageUrl,
      showImageModal: false,
      name: newCompany.name,
      gstNumber: newCompany.gstNumber,
      province: newCompany.province,
      isOnboarding: newCompany.isOnboarding,
    })
  }

  save = async () => {
    this.setState({ isLoading: true })

    const { logoImageUrl, name, gstNumber, province, isOnboarding } = this.state

    try {
      await this.props.currentCompanyStore!.update({
        logoImageUrl,
        name,
        gstNumber,
        province: province || undefined,
        isOnboarding: isOnboarding,
      })
      message.success('Settings successfully saved', 1)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while saving new settings', 1)
    } finally {
      this.setState({ isLoading: false })
    }
  }

  imageOnUpload = async (logoImageUrl: string) => {
    await this.setState({
      logoImageUrl: logoImageUrl,
      showImageModal: false,
    })
    this.save()
  }

  render() {
    const { isLoading, logoImageUrl, showImageModal, name, gstNumber, province, isOnboarding } =
      this.state

    return (
      <Row className='white-rounded-container'>
        <Helmet>
          <title>Settings | General</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Company logo</b>
            </div>
            <div>Will show on your POS shift start screen.</div>
            <br />
            <Button onClick={() => this.setState({ showImageModal: true })}>
              <Icon type='upload' /> Click to upload
            </Button>
          </Col>
          <Col xs={12} lg={{ offset: 1, span: 4 }}>
            {logoImageUrl ? (
              <div>
                <img
                  className='mb-2'
                  alt='company-logo'
                  src={logoImageUrl}
                  style={{ width: '10em', height: '10em' }}
                />
              </div>
            ) : (
              <span />
            )}
            <div></div>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Company name</b>
            </div>
            <div>
              Will show up on the top of your{' '}
              <a href={Routes.DASHBOARD_SETTINGS_RECEIPTS(this.company.id)}>receipts</a>.
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 1, span: 4 }}>
            <Tooltip title='Please contact customer support to change your company name'>
              <Input
                disabled={!this.user.isAdmin}
                value={name}
                onChange={(e) => this.setState({ name: e.target.value })}
              />
            </Tooltip>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Province</b>
            </div>
            <div>Will change your compliance report settings.</div>
          </Col>
          <Col xs={12} lg={{ offset: 1, span: 4 }}>
            <Select<Province | null>
              allowClear
              style={{ width: 120 }}
              defaultValue={province}
              onChange={(province) => {
                this.setState({ province: province || null })
              }}
            >
              {['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NS', 'NB', 'PE', 'NL', 'YT', 'NT', 'NU'].map(
                (province, index) => {
                  return (
                    <Select.Option key={index} value={province}>
                      {province}
                    </Select.Option>
                  )
                },
              )}
            </Select>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>GST number</b>
            </div>
            <div>
              Will show up on the bottom of your{' '}
              <a href={Routes.DASHBOARD_SETTINGS_RECEIPTS(this.company.id)}>receipts</a>.
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 1, span: 4 }}>
            <Input
              value={gstNumber || ''}
              onChange={(e) => this.setState({ gstNumber: e.target.value })}
            />
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Onboarding mode</b>
            </div>
            <div>
              Will show a first-time user checklist on the dashboard homepage. Can be turned off at
              anytime.
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 1, span: 4 }}>
            <Switch
              defaultChecked={isOnboarding}
              onChange={(value) => this.setState({ isOnboarding: value })}
            />
          </Col>
        </Row>

        <Button type='primary' loading={isLoading} onClick={() => this.save()}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>

        {showImageModal && (
          <ImageUploadModal
            isOpen={showImageModal}
            onHide={() => this.setState({ showImageModal: false })}
            onUpload={(object) => this.imageOnUpload(object.imageUrl)}
          />
        )}
      </Row>
    )
  }
}
